import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Lock,
  Search,
  Download,
  Trash2,
  RefreshCw,
  LogOut,
  Users,
  Loader2,
  ChevronDown,
  Building2,
  Phone,
  Mail,
  MapPin,
  Globe,
  Pencil,
  Save,
  X,
  UserCircle2,
  Bell,
  Church,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "tuiglesia_admin_pwd";

const STATUS_OPTIONS = [
  { value: "pendiente", label: "Pendiente", color: "bg-amber-100 text-amber-800 border-amber-200" },
  { value: "contactado", label: "Contactado", color: "bg-blue-100 text-blue-800 border-blue-200" },
  { value: "aprobado", label: "Aprobado", color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
];

const TYPE_META = {
  pastor: { label: "Pastor", short: "Pastor", color: "bg-[#056A84]/10 text-[#056A84] border-[#056A84]/20", Icon: Church },
  member_notify: { label: "Miembro · Notificación", short: "Notif.", color: "bg-violet-100 text-violet-800 border-violet-200", Icon: Bell },
  pastor_referral: { label: "Miembro · Refiere Pastor", short: "Referido", color: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200", Icon: UserCircle2 },
};

const TYPE_FILTER_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "pastor", label: "Pastor" },
  { value: "member_notify", label: "Miembro · Notificación" },
  { value: "pastor_referral", label: "Miembro · Refiere Pastor" },
];

export default function AdminPanel() {
  const [password, setPassword] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const authHeaders = useCallback(
    () => ({ headers: { "X-Admin-Password": password } }),
    [password]
  );

  const fetchList = useCallback(
    async (query = "", reg_type = "") => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (query) params.set("q", query);
        if (reg_type) params.set("reg_type", reg_type);
        const qs = params.toString();
        const res = await axios.get(
          `${API}/admin/preregistrations${qs ? `?${qs}` : ""}`,
          authHeaders()
        );
        setItems(res.data.items || []);
      } catch (e) {
        if (e.response?.status === 401) {
          setAuthed(false);
          localStorage.removeItem(STORAGE_KEY);
        }
      } finally {
        setLoading(false);
      }
    },
    [authHeaders]
  );

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      axios
        .post(`${API}/admin/verify`, { password: stored })
        .then(() => {
          setAuthed(true);
          setPassword(stored);
        })
        .catch(() => {
          localStorage.removeItem(STORAGE_KEY);
        });
    }
  }, []);

  useEffect(() => {
    if (authed) fetchList();
  }, [authed, fetchList]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoginLoading(true);
    try {
      await axios.post(`${API}/admin/verify`, { password });
      localStorage.setItem(STORAGE_KEY, password);
      setAuthed(true);
    } catch {
      setLoginError("Contraseña incorrecta");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setPassword("");
    setAuthed(false);
    setItems([]);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchList(search, typeFilter);
  };

  const handleTypeChange = (value) => {
    setTypeFilter(value);
    fetchList(search, value);
  };

  const handleStatusChange = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/preregistrations/${id}/status`, { status }, authHeaders());
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status } : it)));
    } catch (e) {
      alert("No se pudo actualizar el estado");
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`¿Eliminar el pre-registro de "${name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/admin/preregistrations/${id}`, authHeaders());
      setItems((prev) => prev.filter((it) => it.id !== id));
    } catch (e) {
      alert("No se pudo eliminar el registro");
    }
  };

  const handleSaveEdit = async (id, updates) => {
    try {
      const res = await axios.patch(`${API}/admin/preregistrations/${id}`, updates, authHeaders());
      setItems((prev) => prev.map((it) => (it.id === id ? res.data.item : it)));
      return true;
    } catch {
      alert("No se pudo guardar los cambios");
      return false;
    }
  };

  const handleExportCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/preregistrations/export.csv`, {
        ...authHeaders(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "preregistros-tuiglesia.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert("No se pudo exportar el CSV");
    }
  };

  // ============= Login screen =============
  if (!authed) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-5">
        <form
          onSubmit={handleLogin}
          data-testid="admin-login-form"
          className="w-full max-w-sm bg-white rounded-3xl border border-gray-200 p-8 shadow-sm"
        >
          <div className="w-14 h-14 mx-auto mb-5 bg-[#056A84]/10 rounded-2xl flex items-center justify-center">
            <Lock className="w-7 h-7 text-[#056A84]" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-[#111827] text-center mb-2">
            Panel Admin
          </h1>
          <p className="text-sm text-[#4B5563] text-center mb-6">
            Tu Iglesia — Pre-registros de ministerios
          </p>
          <label className="block text-xs font-heading font-semibold text-[#111827] uppercase tracking-wider mb-1.5">
            Contraseña
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            data-testid="admin-password-input"
            className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#00CCFF] focus:border-transparent transition-all"
            autoFocus
          />
          {loginError && (
            <p data-testid="admin-login-error" className="text-sm text-red-600 mt-3 text-center">
              {loginError}
            </p>
          )}
          <button
            type="submit"
            disabled={loginLoading}
            data-testid="admin-login-submit"
            className="mt-5 w-full flex items-center justify-center gap-2 bg-[#00CCFF] text-white font-heading font-bold text-sm py-3.5 px-6 rounded-full hover:bg-[#00b8e6] active:scale-95 transition-all duration-200 disabled:opacity-70"
          >
            {loginLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            Entrar
          </button>
        </form>
      </div>
    );
  }

  // ============= Admin dashboard =============
  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-panel">
      {/* Top bar */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#056A84]/10 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-[#056A84]" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-[#111827] text-base leading-tight">
                Panel Admin
              </h1>
              <p className="text-xs text-gray-500">Pre-registros ministeriales</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchList(search, typeFilter)}
              data-testid="admin-refresh-btn"
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              title="Actualizar"
            >
              <RefreshCw className={`w-4 h-4 text-gray-600 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={handleExportCsv}
              data-testid="admin-export-csv-btn"
              className="flex items-center gap-2 bg-[#056A84] text-white font-heading font-semibold text-xs uppercase tracking-wider px-4 py-2 rounded-full hover:bg-[#045670] transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              CSV
            </button>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              title="Cerrar sesión"
            >
              <LogOut className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-6">
        {/* Stats + search */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="bg-white border border-gray-200 rounded-2xl px-5 py-3">
              <p className="text-xs uppercase tracking-wider text-gray-500 font-heading font-semibold">
                Total registros
              </p>
              <p data-testid="admin-total-count" className="font-heading text-2xl font-bold text-[#111827]">
                {items.length}
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl px-5 py-3">
              <p className="text-xs uppercase tracking-wider text-gray-500 font-heading font-semibold">
                Meta
              </p>
              <p className="font-heading text-2xl font-bold text-[#056A84]">50</p>
            </div>
          </div>

          <form onSubmit={handleSearch} className="flex items-center gap-2 flex-1 md:max-w-xl">
            <select
              value={typeFilter}
              onChange={(e) => handleTypeChange(e.target.value)}
              data-testid="admin-type-filter"
              className="px-3 py-2.5 rounded-full border border-gray-200 bg-white text-sm text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#00CCFF] focus:border-transparent"
            >
              {TYPE_FILTER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar..."
                data-testid="admin-search-input"
                className="w-full pl-10 pr-4 py-2.5 rounded-full border border-gray-200 bg-white text-sm text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#00CCFF] focus:border-transparent"
              />
            </div>
            <button
              type="submit"
              data-testid="admin-search-submit"
              className="bg-[#00CCFF] text-white font-heading font-semibold text-xs uppercase tracking-wider px-5 py-2.5 rounded-full hover:bg-[#00b8e6] transition-colors"
            >
              Buscar
            </button>
          </form>
        </div>

        {/* List */}
        {loading && items.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-[#056A84]" />
          </div>
        ) : items.length === 0 ? (
          <div
            data-testid="admin-empty-state"
            className="text-center py-16 bg-white rounded-2xl border border-gray-200"
          >
            <Users className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            <p className="text-sm text-gray-500">
              {search ? "No se encontraron registros que coincidan." : "Aún no hay pre-registros."}
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-preregistrations-list">
            {items.map((item) => (
              <RegistrationCard
                key={item.id}
                item={item}
                expanded={expandedId === item.id}
                onToggle={() => setExpandedId(expandedId === item.id ? null : item.id)}
                onStatusChange={handleStatusChange}
                onDelete={handleDelete}
                onSaveEdit={handleSaveEdit}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function RegistrationCard({ item, expanded, onToggle, onStatusChange, onDelete, onSaveEdit }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item);
  const [saving, setSaving] = useState(false);
  const status = STATUS_OPTIONS.find((s) => s.value === (item.status || "pendiente")) || STATUS_OPTIONS[0];
  const regType = item.registration_type || "pastor";
  const typeMeta = TYPE_META[regType] || TYPE_META.pastor;
  const TypeIcon = typeMeta.Icon;
  const isMemberNotify = regType === "member_notify";
  const isReferral = regType === "pastor_referral";
  const date = new Date(item.created_at).toLocaleDateString("es-VE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  // Card title: member name for notify, ministry for the rest
  const headerTitle = isMemberNotify
    ? item.member_name || "Miembro"
    : item.ministry_name || "Sin nombre";
  const headerSubtitle = isMemberNotify
    ? `Esperando notificación${item.city ? ` · ${item.city}` : ""}`
    : `${item.pastor_name || "—"}${item.city ? ` · ${item.city}` : ""}`;

  const startEdit = () => {
    setDraft(item);
    setEditing(true);
  };
  const cancelEdit = () => {
    setDraft(item);
    setEditing(false);
  };
  const saveEdit = async () => {
    setSaving(true);
    const updates = {};
    Object.keys(draft).forEach((k) => {
      if (draft[k] !== item[k]) updates[k] = draft[k];
    });
    if (Object.keys(updates).length === 0) {
      setEditing(false);
      setSaving(false);
      return;
    }
    const ok = await onSaveEdit(item.id, updates);
    setSaving(false);
    if (ok) setEditing(false);
  };
  const setDraftField = (name, value) => setDraft((p) => ({ ...p, [name]: value }));

  return (
    <div
      data-testid={`registration-card-${item.id}`}
      className="bg-white border border-gray-200 rounded-2xl overflow-hidden transition-shadow hover:shadow-md"
    >
      <div className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <button onClick={onToggle} className="flex-1 text-left min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <TypeIcon className="w-4 h-4 text-[#056A84] flex-shrink-0" />
              <h3 className="font-heading font-bold text-[#111827] truncate">
                {headerTitle}
              </h3>
              <span
                data-testid={`type-badge-${item.id}`}
                className={`text-[9px] uppercase tracking-wider font-heading font-semibold px-2 py-0.5 rounded-full border ${typeMeta.color}`}
              >
                {typeMeta.short}
              </span>
            </div>
            <p className="text-sm text-[#4B5563] truncate">{headerSubtitle}</p>
            <p className="text-xs text-gray-400 mt-0.5">{date}</p>
          </button>

          <div className="flex items-center gap-2 flex-shrink-0">
            <span
              className={`text-[10px] uppercase tracking-wider font-heading font-semibold px-2.5 py-1 rounded-full border ${status.color}`}
            >
              {status.label}
            </span>
            <button
              onClick={onToggle}
              data-testid={`toggle-${item.id}`}
              className="p-1.5 rounded-lg hover:bg-gray-100"
            >
              <ChevronDown
                className={`w-4 h-4 text-gray-500 transition-transform ${expanded ? "rotate-180" : ""}`}
              />
            </button>
          </div>
        </div>

        {expanded && !editing && (
          <div className="mt-5 pt-5 border-t border-gray-100 space-y-4 text-sm">
            {/* Member info section (for notify + referral) */}
            {(isMemberNotify || isReferral) && (
              <div className="bg-violet-50 border border-violet-100 rounded-xl p-3">
                <p className="text-[10px] uppercase tracking-wider font-heading font-semibold text-violet-700 mb-2">
                  {isMemberNotify ? "Miembro a notificar" : "Miembro que refiere"}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <DetailRow icon={<UserCircle2 className="w-4 h-4" />} label="Nombre" value={item.member_name || <span className="text-gray-400">—</span>} />
                  <DetailRow icon={<Mail className="w-4 h-4" />} label="Email" value={item.member_email || <span className="text-gray-400">—</span>} />
                  <DetailRow icon={<Phone className="w-4 h-4" />} label="Teléfono" value={item.member_phone || <span className="text-gray-400">—</span>} />
                  {isMemberNotify && (
                    <DetailRow icon={<MapPin className="w-4 h-4" />} label="Ciudad" value={item.city || <span className="text-gray-400">—</span>} />
                  )}
                </div>
              </div>
            )}

            {/* Pastor / ministry info (hidden for pure member_notify) */}
            {!isMemberNotify && (
              <div className={isReferral ? "bg-[#056A84]/5 border border-[#056A84]/15 rounded-xl p-3" : ""}>
                {isReferral && (
                  <p className="text-[10px] uppercase tracking-wider font-heading font-semibold text-[#056A84] mb-2">
                    Pastor referido
                  </p>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <DetailRow icon={<Church className="w-4 h-4" />} label="Ministerio" value={item.ministry_name || <span className="text-gray-400">—</span>} />
                  <DetailRow icon={<UserCircle2 className="w-4 h-4" />} label="Pastor" value={item.pastor_name || <span className="text-gray-400">—</span>} />
                  <DetailRow icon={<Mail className="w-4 h-4" />} label="Email" value={item.email || <span className="text-gray-400">—</span>} />
                  <DetailRow icon={<Phone className="w-4 h-4" />} label="Teléfono" value={item.phone || <span className="text-gray-400">—</span>} />
                  <DetailRow icon={<MapPin className="w-4 h-4" />} label="Ciudad" value={item.city ? `${item.city}, ${item.country || "Venezuela"}` : <span className="text-gray-400">—</span>} />
                  {!isReferral && (
                    <>
                      <DetailRow icon={<Users className="w-4 h-4" />} label="Tamaño" value={item.congregation_size || <span className="text-gray-400">—</span>} />
                      <DetailRow icon={<Building2 className="w-4 h-4" />} label="Denominación" value={item.denomination || <span className="text-gray-400">—</span>} />
                      <DetailRow
                        icon={<Globe className="w-4 h-4" />}
                        label="Sitio web"
                        value={
                          item.website ? (
                            <a href={item.website} target="_blank" rel="noreferrer" className="text-[#056A84] hover:underline break-all">
                              {item.website}
                            </a>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )
                        }
                      />
                    </>
                  )}
                </div>
                {!isReferral && item.has_multiple_congregations && (
                  <div className="mt-3 bg-[#00CCFF]/5 border border-[#00CCFF]/20 rounded-xl p-3">
                    <p className="text-xs uppercase font-heading font-semibold text-[#056A84] mb-1">
                      Múltiples congregaciones
                    </p>
                    <p className="text-sm text-[#4B5563]">
                      {item.multiple_congregations_details || "Sin detalles"}
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2 pt-2">
              <span className="text-xs uppercase font-heading font-semibold text-gray-500 mr-2">
                Estado:
              </span>
              {STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => onStatusChange(item.id, opt.value)}
                  data-testid={`set-status-${opt.value}-${item.id}`}
                  className={`text-xs font-heading font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                    item.status === opt.value
                      ? opt.color
                      : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
              <div className="ml-auto flex items-center gap-2">
                <button
                  onClick={startEdit}
                  data-testid={`edit-${item.id}`}
                  className="flex items-center gap-1.5 text-xs font-heading font-semibold text-[#056A84] px-3 py-1.5 rounded-full border border-[#056A84]/30 hover:bg-[#056A84]/5 transition-colors"
                >
                  <Pencil className="w-3.5 h-3.5" />
                  Editar
                </button>
                <button
                  onClick={() => onDelete(item.id, headerTitle)}
                  data-testid={`delete-${item.id}`}
                  className="flex items-center gap-1.5 text-xs font-heading font-semibold text-red-600 px-3 py-1.5 rounded-full border border-red-200 hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        )}

        {expanded && editing && (
          <div className="mt-5 pt-5 border-t border-gray-100 space-y-3" data-testid={`edit-form-${item.id}`}>
            {(isMemberNotify || isReferral) && (
              <div className="bg-violet-50 border border-violet-100 rounded-xl p-3 space-y-3">
                <p className="text-[10px] uppercase tracking-wider font-heading font-semibold text-violet-700">
                  {isMemberNotify ? "Miembro a notificar" : "Miembro que refiere"}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <EditInput label="Nombre miembro" value={draft.member_name || ""} onChange={(v) => setDraftField("member_name", v)} />
                  <EditInput label="Email miembro" value={draft.member_email || ""} onChange={(v) => setDraftField("member_email", v)} type="email" />
                  <EditInput label="Teléfono miembro" value={draft.member_phone || ""} onChange={(v) => setDraftField("member_phone", v)} type="tel" />
                </div>
              </div>
            )}
            {!isMemberNotify && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <EditInput label="Ministerio" value={draft.ministry_name || ""} onChange={(v) => setDraftField("ministry_name", v)} />
                <EditInput label="Pastor" value={draft.pastor_name || ""} onChange={(v) => setDraftField("pastor_name", v)} />
                <EditInput label="Email pastor" value={draft.email || ""} onChange={(v) => setDraftField("email", v)} type="email" />
                <EditInput label="Teléfono pastor" value={draft.phone || ""} onChange={(v) => setDraftField("phone", v)} type="tel" />
                <EditInput label="Ciudad" value={draft.city || ""} onChange={(v) => setDraftField("city", v)} />
                {!isReferral && (
                  <>
                    <EditInput label="Denominación" value={draft.denomination || ""} onChange={(v) => setDraftField("denomination", v)} />
                    <div>
                      <label className="block text-[10px] uppercase tracking-wider font-heading font-semibold text-gray-500 mb-1">
                        Tamaño
                      </label>
                      <select
                        value={draft.congregation_size || ""}
                        onChange={(e) => setDraftField("congregation_size", e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00CCFF] focus:border-transparent"
                      >
                        <option value="">—</option>
                        <option value="1-100">1 - 100</option>
                        <option value="101-300">101 - 300</option>
                        <option value="301-600">301 - 600</option>
                        <option value="600+">Más de 600</option>
                      </select>
                    </div>
                    <EditInput label="Sitio web" value={draft.website || ""} onChange={(v) => setDraftField("website", v)} type="url" />
                  </>
                )}
              </div>
            )}
            {isMemberNotify && (
              <EditInput label="Ciudad" value={draft.city || ""} onChange={(v) => setDraftField("city", v)} />
            )}
            {!isMemberNotify && !isReferral && (
              <>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!draft.has_multiple_congregations}
                    onChange={(e) => setDraftField("has_multiple_congregations", e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-[#056A84] focus:ring-[#00CCFF]"
                  />
                  <span className="text-sm text-[#4B5563]">Múltiples congregaciones</span>
                </label>
                {draft.has_multiple_congregations && (
                  <textarea
                    value={draft.multiple_congregations_details || ""}
                    onChange={(e) => setDraftField("multiple_congregations_details", e.target.value)}
                    rows={2}
                    placeholder="Detalles de las otras congregaciones..."
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00CCFF] focus:border-transparent resize-none"
                  />
                )}
              </>
            )}
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                onClick={cancelEdit}
                disabled={saving}
                data-testid={`cancel-edit-${item.id}`}
                className="flex items-center gap-1.5 text-xs font-heading font-semibold text-gray-600 px-4 py-2 rounded-full border border-gray-200 hover:bg-gray-50"
              >
                <X className="w-3.5 h-3.5" />
                Cancelar
              </button>
              <button
                onClick={saveEdit}
                disabled={saving}
                data-testid={`save-edit-${item.id}`}
                className="flex items-center gap-1.5 text-xs font-heading font-semibold text-white bg-[#00CCFF] px-4 py-2 rounded-full hover:bg-[#00b8e6] disabled:opacity-70"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Guardar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function EditInput({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider font-heading font-semibold text-gray-500 mb-1">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00CCFF] focus:border-transparent"
      />
    </div>
  );
}

function DetailRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <div className="text-gray-400 mt-0.5">{icon}</div>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider font-heading font-semibold text-gray-500">
          {label}
        </p>
        <p className="text-sm text-[#111827] break-words">{value}</p>
      </div>
    </div>
  );
}
